import {
  forwardRef,
  HTMLAttributes,
  useCallback,
  useEffect,
  useRef
} from 'react'
import { TransitionStatus } from 'react-transition-group'
import gsap from 'gsap'
import { Draggable } from 'gsap/dist/Draggable'
import Lenis from 'lenis'
import { forEach } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Typography from '@/components/base/typography'
import LanguageList from '@/components/layout/language-list'
import { useStore } from '@/contexts/store'
import { useLocaleStore } from '@/store/locale'
import { isBookingLink, stripSlash } from '@/utils/path'

import { cn } from '@/lib/utils'

export type MenuProps = HTMLAttributes<HTMLDivElement> & {
  state: TransitionStatus
}

export const MobileMenu = forwardRef<HTMLDivElement, MenuProps>(
  ({ state, className, ...props }, ref) => {
    const router = useRouter()
    const { menu, strings } = useStore()
    const { footer, links } = strings || {}

    const currentLocale = useLocaleStore((state) => state.currentLocale)

    const isActive = useCallback(
      (url: string) =>
        stripSlash(url) ===
        stripSlash(
          `${currentLocale !== router.defaultLocale ? `/${currentLocale}` : ''}${router.asPath}`
        ),
      [router, currentLocale]
    )

    return (
      <div
        ref={ref}
        className={cn(
          'fixed left-0 top-0 z-menu h-[100lvh] w-full bg-black',
          'transition-opacity duration-500 ease-in-out-cubic',
          {
            'opacity-0 duration-0': state === 'entering',
            'opacity-100': state === 'entered',
            'opacity-0': state === 'exiting'
          },
          className
        )}
        {...props}
      >
        <div
          className={cn(
            'flex h-[100dvh] w-full flex-col justify-between px-margin pb-[2rem] pt-[1rem]',
            'before:h-0 before:w-full'
          )}
        >
          <nav className="relative pl-[1rem]">
            <ul className="flex flex-col space-y-[0.6rem]">
              {menu?.main_menu?.menu.map(({ url, title }, index) => (
                <li
                  key={`menu-item-${index}`}
                  className={cn(
                    'relative',
                    'before:absolute before:-left-[3rem] before:top-0 before:h-[3rem] before:w-[0.5rem] before:scale-y-0 before:bg-white',
                    'before:transition-transform before:duration-500 before:ease-in-out-cubic',
                    {
                      'before:scale-y-100': isActive(url)
                    }
                  )}
                >
                  <Link
                    href={url}
                    scroll={false}
                    className={cn(
                      'transition-colors duration-300 ease-out-cubic',
                      {
                        'pointer-events-none': isActive(url)
                      }
                    )}
                    data-gtm-event={
                      isBookingLink(url) ? 'book-cta' : 'menu-item'
                    }
                  >
                    <Typography className="pointer-events-none text-[2rem] capitalize tracking-[0.06rem]">
                      {title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="grid gap-y-[2rem]">
            <div className="flex flex-col items-start gap-y-[2rem]">
              <LanguageList />

              {menu?.social_menu?.menu.map(({ url, title, target }, index) => (
                <li key={`social-item-${index}`} className="list-none">
                  <Link
                    scroll={false}
                    href={url}
                    target={target}
                    className="-m-[1rem] flex p-[1rem]"
                  >
                    <Typography className="text-[1.28rem] font-bold capitalize tracking-[0.0384rem] text-white/80">
                      {title}
                    </Typography>
                  </Link>
                </li>
              ))}
              {menu?.legals_menu?.menu.map(({ url, title, target }, index) => {
                return (
                  <li key={`legals-item-${index}`} className="list-none">
                    <Link
                      scroll={false}
                      href={url}
                      target={target}
                      className="-m-[1rem] flex p-[1rem]"
                    >
                      <Typography className="text-[1.28rem] font-bold capitalize tracking-[0.0384rem] text-white/80">
                        {title}
                      </Typography>
                    </Link>
                  </li>
                )
              })}

              {links?.blog.url ? (
                <Link
                  scroll={false}
                  href={links?.blog.url}
                  target={links?.blog.target}
                  className="flex"
                >
                  <Typography className="text-[1.28rem] font-bold capitalize tracking-[0.0384rem]">
                    {links?.blog.title}
                  </Typography>
                </Link>
              ) : null}
            </div>

            <div className="flex flex-col">
              <Typography className="text-[1.1rem] capitalize tracking-[0.027rem] text-white/40">
                {footer?.colophon.replace('[YEAR]', new Date().getFullYear())}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export const CardsMenu = forwardRef<HTMLDivElement, MenuProps>(
  ({ state, className, ...props }, ref) => {
    const router = useRouter()
    const { menu, strings } = useStore()
    const { menu_assets } = strings || {}
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)
    const slides = useRef<HTMLLIElement[]>([])
    const currentLocale = useLocaleStore((state) => state.currentLocale)

    const isActive = useCallback(
      (url: string) =>
        stripSlash(url) ===
        stripSlash(
          `${currentLocale !== router.defaultLocale ? `/${currentLocale}` : ''}${router.asPath}`
        ),
      [router, currentLocale]
    )

    useEffect(() => {
      const lenis = new Lenis({
        smoothWheel: true,
        eventsTarget: document.documentElement,
        wrapper: wrapper.current,
        content: content.current,
        gestureOrientation: 'both',
        orientation: 'horizontal'
      })

      const draggable = new Draggable(wrapper.current, {
        type: 'scrollLeft',
        bounds: wrapper.current,
        dragClickables: true,
        cursor: 'default',
        inertia: true,
        onDragStart: () => lenis.stop(),
        onDragEnd: () => lenis.start()
      })

      const tick = (time: number) => {
        lenis.raf(time * 1000)
      }

      gsap.ticker.add(tick)

      forEach(menu?.main_menu?.menu, (item, index) => {
        const slide = slides.current[index]
        const active = isActive(item.url)
        active && lenis.scrollTo(slide.offsetLeft, { immediate: true })
      })

      return () => {
        gsap.ticker.remove(tick)
        draggable.kill()
        lenis.destroy()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <div
        ref={ref}
        className={cn(
          'fixed left-0 top-0 z-menu h-[100lvh] w-full bg-dark',
          'transition-opacity ease-in-out-cubic',
          {
            'opacity-0 duration-0': state === 'entering',
            'opacity-100 delay-0 duration-1000': state === 'entered',
            'opacity-0 delay-200 duration-800': state === 'exiting'
          },
          className
        )}
        {...props}
      >
        <div
          ref={wrapper}
          className="absolute left-0 top-0 flex h-full w-full items-center overflow-auto scrollbar-hide"
        >
          <nav ref={content} className="relative h-auto w-full select-none">
            <ul className="flex touch-pan-y">
              {menu?.main_menu?.menu.map(({ url, title }, index) => (
                <li
                  key={`menu-item-${index}`}
                  ref={(el) => (slides.current[index] = el as any)}
                  className="relative aspect-[400/450] min-w-0 shrink-0 grow-0 basis-[calc(400/450_*_55vh)]"
                >
                  <Link
                    scroll={false}
                    href={url}
                    className={cn('absolute left-0 top-0 h-full w-full', {
                      'pointer-events-none': isActive(url)
                    })}
                    data-gtm-event={
                      isBookingLink(url) ? 'book-cta' : 'menu-item'
                    }
                  >
                    <div className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden">
                      <div
                        className={cn(
                          'absolute left-0 top-0 h-full w-full p-[2rem]',
                          'before:absolute before:left-1/2 before:top-1/2 before:h-[calc(100%-5rem)] before:w-[calc(100%-5rem)] before:-translate-x-1/2 before:-translate-y-1/2 before:bg-white',
                          'before:transition-all before:duration-500 before:ease-in-out-cubic',
                          'transition-transform ease-in-out-cubic',
                          {
                            'scale-0 duration-0': state === 'entering',
                            'scale-100 duration-1200': state === 'entered',
                            'translate-y-full scale-50 duration-900':
                              state === 'exiting'
                          },
                          {
                            'before:h-full before:w-full': isActive(url)
                          }
                        )}
                        style={{
                          transitionDelay:
                            state === 'entered'
                              ? `${100 + 80 * index}ms`
                              : '0ms'
                        }}
                      >
                        <video
                          playsInline={true}
                          autoPlay={true}
                          loop={true}
                          muted={true}
                          preload="auto"
                          className={cn(
                            'relative h-full w-full transform-gpu bg-black object-cover grayscale',
                            'transition-all duration-500 ease-in-out-cubic',
                            'hover:grayscale-0',
                            {
                              'grayscale-0': isActive(url)
                            }
                          )}
                        >
                          <source
                            src={menu_assets.list[index].video.url}
                            type="video/mp4"
                          />
                        </video>
                      </div>
                    </div>

                    <div className="pointer-events-none absolute -bottom-[3.1rem] left-0 overflow-hidden">
                      <Typography
                        className={cn(
                          'flex px-[2rem] text-[2rem] uppercase leading-[4.2rem] tracking-[0.1rem]',
                          'leading-[1] mix-blend-exclusion transition-transform',
                          {
                            'translate-y-full duration-0': state === 'entering',
                            'translate-y-0 duration-1000 ease-out-cubic':
                              state === 'entered',
                            'translate-y-full duration-700 ease-in-out-cubic':
                              state === 'exiting'
                          }
                        )}
                        style={{
                          transitionDelay:
                            state === 'entered'
                              ? `${800 + 100 * index}ms`
                              : '0ms'
                        }}
                      >
                        {title}
                      </Typography>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <ul
          className={cn(
            'absolute bottom-[3.6rem] right-[4rem]',
            'flex items-center space-x-[2rem]'
          )}
        >
          <LanguageList />

          {menu?.social_menu?.menu.map(({ url, title, target }, index) => (
            <li key={`social-item-${index}`}>
              <Link
                scroll={false}
                href={url}
                target={target}
                className="-m-[1rem] flex p-[1rem]"
              >
                <Typography className="text-[1.28rem] font-bold capitalize tracking-[0.0384rem] text-white/30">
                  {title}
                </Typography>
              </Link>
            </li>
          ))}
          {menu?.legals_menu?.menu.map(({ url, title, target }, index) => {
            return (
              <li key={`legals-item-${index}`}>
                <Link
                  scroll={false}
                  href={url}
                  target={target}
                  className="-m-[1rem] flex p-[1rem]"
                >
                  <Typography className="text-[1.28rem] font-bold capitalize tracking-[0.0384rem] text-white/30">
                    {title}
                  </Typography>
                </Link>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }
)
